import React from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Lightbulb } from "lucide-react";
import { TspBreadcrumb, TspEmptyState, TspSkeleton } from "../../components/primitives";
import TspLoadError from "../../components/common/TspLoadError";
import TspApiService from "../../api/tspApiService";
import useTspApiResource from "../../hooks/useTspApiResource";
import { useTspAdviceItem } from "./useTspAdviceData";
import { EMPTY_ADVICE_FORM, adviceToFormValues } from "./adviceModel";
import AdviceForm from "./components/AdviceForm";

// Add (/tsp/advice/new) and Edit (/tsp/advice/:adviceId/edit). "New" can be
// prefilled from the URL (?location_id=<id>&journey_id=<id>) — the Knowledge
// city view and the Journey Detail page use this for "Add advice".
export default function TspAdviceFormPage() {
  const { adviceId } = useParams();
  const [searchParams] = useSearchParams();
  const isEdit = Boolean(adviceId);
  const advice = useTspAdviceItem(adviceId);
  const journeys = useTspApiResource(
    () => (isEdit ? Promise.resolve({ data: [] }) : TspApiService.listJourneys({ status: "IN_PROGRESS", sort: "journey_name", order: "asc", limit: 100 })),
    [isEdit]
  );
  const crumbs = [{ label: "Dashboard", to: "/tsp" }, { label: "Advice Repository", to: "/tsp/advice" }, isEdit ? "Edit Advice" : "Add Advice"];

  const error = advice.error || journeys.error;
  if (error) {
    const notFound = error?.response?.status === 404;
    return (
      <div className="tsp-page tsp-advice-page">
        <TspBreadcrumb items={crumbs} />
        {notFound ? (
          <TspEmptyState
            icon={Lightbulb}
            title="Advice not found"
            message="This advice may have been removed, or the link is incorrect."
            action={
              <Link to="/tsp/advice" className="tsp-btn tsp-btn-outline-primary">
                <ArrowLeft size={14} /> Back to Advice Repository
              </Link>
            }
          />
        ) : (
          <TspLoadError error={error} onRetry={() => (advice.error ? advice.reload() : journeys.reload())} what="this form" />
        )}
      </div>
    );
  }

  if (advice.loading || journeys.loading || !journeys.data) {
    return (
      <div className="tsp-page tsp-advice-page">
        <TspBreadcrumb items={crumbs} />
        <TspSkeleton variant="row" count={4} label="Loading form" />
      </div>
    );
  }

  const preset = searchParams.get("journey_id");
  const initialValues = isEdit
    ? adviceToFormValues(advice.item)
    : { ...EMPTY_ADVICE_FORM, journeyId: journeys.data.some((journey) => String(journey.id) === preset) ? preset : "" };

  return <AdviceForm key={adviceId || "new"} mode={isEdit ? "edit" : "new"} initialValues={initialValues} item={advice.item} journeys={journeys.data} presetLocationId={searchParams.get("location_id") || ""} />;
}
